// The run card is opt-in per message via a real, recognizable link appended to the sent content —
// not a hidden marker. This mirrors the platform's insights model (a reference in `content`, the rich
// view re-fetched by id), see docs/quality-run-card.md. The link is a normal markdown anchor to the
// run page, gated by an explicit `card=run` flag so a user who merely pastes a `/quality/<id>` link
// never has their message replaced by a card. `via` carries the action mode. If detection is ever
// bypassed the link still renders as a plain clickable anchor (graceful — never blanks the message).

export type RunActionMode =
  | 'analyze'
  | 'create-task'
  | 'file_defects'
  | 'rework_cases'
  | 'report_qa_errors';

/** Markdown link appended to the message content: the card trigger + the run reference. */
export function buildRunCardRef(
  runId: string,
  runName: string,
  mode: RunActionMode,
): string {
  return `\n\n[${runName}](/quality/${encodeURIComponent(runId)}?card=run&via=${mode})`;
}

export interface RunCardRef {
  runId: string;
  mode: RunActionMode;
  label: string | null;
}

// `[label](/quality/<id>?<query>)`. The id excludes whitespace, parens and `?`; the query excludes
// whitespace and the closing paren. Query-param order is irrelevant — we parse it with URLSearchParams.
const REF_RE = /\[([^\]]*)\]\(\/quality\/([^()?\s]+)\?([^()\s]*)\)/;

/**
 * Find the producer's run-reference link in a message body. Returns the run id, action mode, and the
 * link label, or null when absent / not gated by `card=run` / malformed. Never throws (invariant: a
 * bad reference must never blank or break a message — it just falls back to normal rendering).
 */
export function parseRunCardRef(body: string): RunCardRef | null {
  try {
    const match = REF_RE.exec(body);
    if (!match) return null;
    const [, rawLabel, rawId, query] = match;
    const params = new URLSearchParams(query);
    if (params.get('card') !== 'run') return null;
    const runId = decodeURIComponent(rawId ?? '');
    if (runId.length === 0) return null;
    const via = params.get('via');
    const mode: RunActionMode =
      via === 'create-task' ||
      via === 'file_defects' ||
      via === 'rework_cases' ||
      via === 'report_qa_errors'
        ? via
        : 'analyze';
    const label = rawLabel && rawLabel.length > 0 ? rawLabel : null;
    return { runId, mode, label };
  } catch {
    return null;
  }
}
